'use client';

import Image from "next/image";
import styles from "./DraggableWindow.module.scss";
import DecorativeVideo from "@/components/DecorativeVideo";
import { useDraggable } from "@/lib/hooks/useDraggable";
import { getTitleFontSize } from "@/lib/utils/typography";
import { getMediaKind, isAnimatedImageUrl } from "@/lib/utils/mediaKind";

interface Props {
  title: string;
  src: string;
  initialPosition: { x: number; y: number };
  size: { width: number; height: number };
  allowDragging: boolean;
  constrainingFunction?: (pos: { x: number; y: number }, size: { width: number; height: number }) => { x: number; y: number };
  href?: string;
  autoplay?: boolean;
  animationDelay?: number;
  zIndex?: number;
  onPointerDownCapture?: () => void;
  prevProjectHref?: string;
  nextProjectHref?: string;
  widthContent: number;
  isCollapsing?: boolean;
  onNavigate?: (href: string) => void;
  eager?: boolean;
  mediaAspectRatio?: number;
}

export default function DraggableWindow({ title, src, initialPosition, size, allowDragging, constrainingFunction, href, autoplay = false, animationDelay = 0, zIndex, onPointerDownCapture, prevProjectHref, nextProjectHref, widthContent, isCollapsing = false, onNavigate, eager = false, mediaAspectRatio }: Props) {
  const openProjectOnTap = () => {
    if (!href) {
      return;
    }

    onNavigate?.(href);
  };

  const { position, isPointerDown, onPointerDown } = useDraggable({
    initialPosition,
    allowDragging,
    constrainingFunction,
    size,
    onTap: openProjectOnTap,
  });

  const isVideo = getMediaKind(src) === 'video';

  const handlePointerDownOnElement = (event: React.PointerEvent<HTMLDivElement>) => {
    onPointerDownCapture?.();
    onPointerDown(event);
  };

  const handleClick = (event: React.MouseEvent) => {
    // Stop click events from propagating to parent elements (e.g., ProjectDetail's returnToLandingPage)
    event.stopPropagation();
  };

  const fontSize = getTitleFontSize(widthContent);

  return (
    <div
      className={`${styles.draggableWindowWrapper} ${isCollapsing ? styles['collapseWindow'] : styles['expandWindow']}`}
      style={{ 
        left: position.x, 
        top: position.y,
        width: size.width,
        animationDelay: `${animationDelay}s`,
        zIndex: zIndex
      }}
      onClick={handleClick}
    >
      <div
        className={styles.draggableWindow}
        data-grabbable={allowDragging ? '' : undefined}
        style={{
          cursor: allowDragging ? (isPointerDown ? "grabbing" : "grab") : "pointer", 
          minHeight: size.height,
        }}
        onPointerDown={handlePointerDownOnElement}
      >
        <p className={styles.title} style={{fontSize: fontSize}}>{title}</p>
        <div
          className={styles.mediaContainer}
          style={mediaAspectRatio ? { height: Math.ceil(size.width * mediaAspectRatio) } : undefined}
        >
          {isVideo ? (
            <DecorativeVideo
              className={styles.media}
              src={src}
              initialLoad={autoplay || eager}
              initialPlay={autoplay}
              aria-label={`${title} video`}
            />
          ) : (
            <Image
              className={styles.media}
              src={src}
              alt={title}
              width={2400}
              height={1404}
              sizes="50vw"
              unoptimized={isAnimatedImageUrl(src)}
              loading={eager ? 'eager' : undefined}
            />
          )}
        </div>
      </div>
      {prevProjectHref && onNavigate && (
        <button 
          className={styles['navigation-back']} 
          style={{fontSize: fontSize}}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onNavigate(prevProjectHref)}
        >
          BACK
        </button>
      )}
      {nextProjectHref && onNavigate && (
        <button 
          className={styles['navigation-next']} 
          style={{fontSize: fontSize}}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onNavigate(nextProjectHref)}
        >
          NEXT
        </button>
      )}
    </div>
  );
}
